/**
 * Formattazione e conversione locale (CF02/CF03) — CL04.
 *
 * Il contratto trasporta i decimali come **stringhe canoniche**: nessun
 * esponente, nessuno zero frazionario finale, nessun `-0`. Qui si fa il lavoro
 * opposto e quello inverso — mostrare all'utente e rileggere da lui — **senza
 * mai passare da `Number`**, che su un importo introduce il float che tutto il
 * resto del dominio evita.
 *
 * La distinzione che regge il modulo:
 *
 * - il **canonico** è ciò che viaggia e si memorizza: `"24"`, `"1000.5"`;
 * - il **mostrato** è ciò che l'utente vede: `24,00`, `1.000,50 €`, `22 %`;
 * - il **compilato** è ciò che l'utente digita: `1.000,50`, `24,`.
 *
 * Solo il primo è autorevole. Gli altri due sono presentazione, e la
 * conversione fra loro è l'unico posto dove un locale può cambiare un numero.
 */

import type { FieldValue } from "model/CustomFields";

export interface DecimalFormat {
  /** Cifre decimali da mostrare. I decimali canonici non ne portano. */
  precision: number;
  decimalSeparator: string;
  thousandsSeparator: string;
  symbol?: string | null;
  symbolPosition?: "before" | "after";
}

const CANONICAL_DECIMAL = /^-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?$/;

/** Un valore è un decimale canonico? */
export const isCanonicalDecimal = (value: unknown): value is string =>
  typeof value === "string" && CANONICAL_DECIMAL.test(value);

/** Divide un canonico in segno, cifre intere e cifre frazionarie. */
function parts(value: string): { negative: boolean; int: string; frac: string } {
  const negative = value.startsWith("-");
  const [int, frac = ""] = value.replace(/^-/, "").split(".");
  return { negative, int, frac };
}

/**
 * Mostra un decimale canonico nel formato del campo.
 *
 * Il canonico non porta zeri decorativi — `"24"` e non `"24.00"` — quindi la
 * precisione si applica **qui**: è il campo a dire quante cifre mostrare, e il
 * trasporto non le trasporta.
 */
export function formatDecimal(canonical: string, format: DecimalFormat): string {
  if (!isCanonicalDecimal(canonical)) return canonical;

  const { negative, int, frac } = parts(canonical);
  const padded = frac.padEnd(format.precision, "0").slice(0, format.precision);
  const grouped = groupThousands(int, format.thousandsSeparator);
  const number = format.precision > 0 ? `${grouped}${format.decimalSeparator}${padded}` : grouped;

  const signed = negative && Number(int) !== 0 ? `-${number}` : number;
  return applySymbol(signed, format);
}

function groupThousands(int: string, separator: string): string {
  if (!separator) return int;
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

function applySymbol(number: string, format: DecimalFormat): string {
  if (!format.symbol) return number;
  return format.symbolPosition === "after"
    ? `${number} ${format.symbol}`
    : `${format.symbol} ${number}`;
}

/**
 * Rilegge ciò che l'utente ha digitato e produce il canonico, o `null`.
 *
 * Riconosce i separatori configurati — non un locale indovinato — e accetta la
 * virgola decimale in coda mentre si digita (`"24,"`), che è uno stato normale
 * di un campo numerico e non un errore da segnalare.
 *
 * Non arrotonda: troppe cifre decimali sono un errore, non una cosa da
 * nascondere silenziosamente.
 */
export function parseDecimal(input: string, format: DecimalFormat): string | null {
  const trimmed = input.trim();
  if (trimmed === "") return null;

  const cleaned = format.symbol ? trimmed.split(format.symbol).join("").trim() : trimmed;

  const negative = cleaned.startsWith("-");
  const body = negative ? cleaned.slice(1) : cleaned;
  // Il segno vale solo in testa: `--1` non è un numero negativo.
  if (body.includes("-")) return null;

  // Prima il separatore decimale, poi quello delle migliaia: al più uno il
  // primo, e il secondo solo in posizioni valide.
  const [intRaw = "", ...fractionParts] = body.split(format.decimalSeparator);
  if (fractionParts.length > 1) return null;

  const fracRaw = fractionParts[0] ?? "";
  if (!/^\d*$/.test(intRaw.split(format.thousandsSeparator || "").join(""))) return null;
  if (!/^\d*$/.test(fracRaw)) return null;
  // `1.2.3` in italiano sarebbe `123` togliendo i separatori: formalmente
  // corretto e in pratica una reinterpretazione silenziosa di un input
  // malformato.
  if (!groupingIsValid(intRaw, format.thousandsSeparator)) return null;

  const intDigits = intRaw.split(format.thousandsSeparator || "").join("");
  if (fracRaw.length > format.precision) return null;

  const normalisedInt = intDigits === "" ? "0" : intDigits.replace(/^0+(?=\d)/, "");
  const normalisedFrac = fracRaw.replace(/0+$/, "");

  if (normalisedFrac === "" && normalisedInt === "0") return "0";
  const sign = negative ? "-" : "";
  return normalisedFrac ? `${sign}${normalisedInt}.${normalisedFrac}` : `${sign}${normalisedInt}`;
}

/** I separatori delle migliaia stanno a gruppi di tre, dopo il primo. */
function groupingIsValid(intPart: string, separator: string): boolean {
  if (!separator || intPart === "") return true;
  const groups = intPart.split(separator);
  if (groups.length === 1) return true;
  if (groups[0].length < 1 || groups[0].length > 3) return false;
  return groups.slice(1).every((group) => group.length === 3);
}

/**
 * Moltiplica due decimali canonici, esattamente.
 *
 * Sui valori monetari `Number` non è un'opzione, e nemmeno `toFixed` lo è: una
 * moltiplicazione fra decimali è una moltiplicazione fra interi con le scale
 * sommate, e si fa sulle cifre.
 */
export function multiplyDecimals(left: string, right: string): string | null {
  if (!isCanonicalDecimal(left) || !isCanonicalDecimal(right)) return null;

  const a = parts(left);
  const b = parts(right);
  const scale = a.frac.length + b.frac.length;

  const leftDigits = `${a.int}${a.frac}`.replace(/^0+/, "") || "0";
  const rightDigits = `${b.int}${b.frac}`.replace(/^0+/, "") || "0";
  const product = multiplyDigitStrings(leftDigits, rightDigits);

  return assemble(a.negative !== b.negative, product, scale);
}

/** Divide un decimale canonico per un intero positivo, esattamente. */
export function divideDecimalByInteger(value: string, divisor: number): string | null {
  if (!isCanonicalDecimal(value) || !Number.isInteger(divisor) || divisor === 0) return null;

  const { negative, int, frac } = parts(value);
  const digits = `${int}${frac}`;

  // La divisione scende nei decimali: fermarsi alle cifre intere direbbe che
  // `1 / 4` non è esatto, quando lo è. Si aggiungono zeri finché il resto non
  // si annulla, entro la scala massima del contratto.
  let quotient = divideDigitString(digits, divisor);
  let extraScale = 0;
  while (quotient === null && extraScale < MAX_DIVISION_SCALE) {
    extraScale += 1;
    quotient = divideDigitString(`${digits}${"0".repeat(extraScale)}`, divisor);
  }
  if (quotient === null) return null;

  return assemble(negative, quotient, frac.length + extraScale);
}

/** La scala massima di una divisione: la stessa che il contratto ammette. */
const MAX_DIVISION_SCALE = 8;

/** Moltiplica due stringhe di cifre, senza passare da `Number`. */
function multiplyDigitStrings(left: string, right: string): string {
  const result = new Array(left.length + right.length).fill(0);
  for (let i = left.length - 1; i >= 0; i -= 1) {
    for (let j = right.length - 1; j >= 0; j -= 1) {
      const product = Number(left[i]) * Number(right[j]) + result[i + j + 1];
      result[i + j + 1] = product % 10;
      result[i + j] += Math.floor(product / 10);
    }
  }
  return result.join("").replace(/^0+/, "") || "0";
}

/** Divisione lunga su cifre. `null` quando il resto non si annulla. */
function divideDigitString(digits: string, divisor: number): string | null {
  let remainder = 0;
  let quotient = "";
  for (const digit of digits) {
    const current = remainder * 10 + Number(digit);
    quotient += String(Math.floor(current / divisor));
    remainder = current % divisor;
  }
  // Una divisione che non è esatta non produce un canonico: meglio `null` di un
  // troncamento silenzioso.
  if (remainder !== 0) return null;
  return quotient.replace(/^0+/, "") || "0";
}

/** Ricompone segno, cifre e scala in un canonico normalizzato. */
function assemble(negative: boolean, digits: string, scale: number): string {
  const padded = digits.padStart(scale + 1, "0");
  const int = padded.slice(0, padded.length - scale) || "0";
  const frac = scale > 0 ? padded.slice(padded.length - scale) : "";

  const trimmedFrac = frac.replace(/0+$/, "");
  const normalisedInt = int.replace(/^0+(?=\d)/, "");
  if (trimmedFrac === "" && normalisedInt === "0") return "0";

  const sign = negative ? "-" : "";
  return trimmedFrac ? `${sign}${normalisedInt}.${trimmedFrac}` : `${sign}${normalisedInt}`;
}

/**
 * Il valore da **mostrare** in un campo percentuale.
 *
 * Il canonico è in punti percentuali: `22` significa 22%. Il fattore di
 * presentazione cambia solo ciò che si vede — e non cambia mai l'unità che le
 * formule vedono, che resta quella canonica.
 */
export function formatPercent(canonical: string, factor: string, format: DecimalFormat): string {
  const scaled = multiplyDecimals(canonical, factor);
  if (scaled === null) return formatDecimal(canonical, format);
  return `${formatDecimal(scaled, format)} %`;
}

/**
 * Il canonico da un valore **mostrato** in un campo percentuale.
 *
 * Si divide per il fattore prima di applicare la precisione, perché è in punti
 * che il valore viene memorizzato e validato.
 */
export function parsePercent(display: string, factor: string, format: DecimalFormat): string | null {
  const withoutUnit = display.replace("%", "");
  const shown = parseDecimal(withoutUnit, format);
  if (shown === null) return null;
  if (factor === "1") return shown;

  const inverse = inverseFactor(factor);
  if (inverse === null) return null;
  return multiplyDecimals(shown, inverse);
}

/**
 * L'inverso di un fattore decimale, quando è esatto.
 *
 * `0.5` → `2`. Un fattore come `0.3` non ha inverso decimale finito: restituire
 * `null` è meglio che inventare un'approssimazione che sposterebbe il valore di
 * qualche punto base a ogni salvataggio.
 *
 * L'aritmetica resta su `Number` **ma è esatta**: la scala di un fattore è
 * limitata dal contratto a 8, quindi `10^scala` non supera `10^8` e sia la
 * potenza sia il divisore stanno ampiamente nell'intervallo degli interi
 * rappresentabili senza perdita. `BigInt` non è disponibile col target di
 * questo progetto, e non serve.
 */
function inverseFactor(factor: string): string | null {
  const { int, frac } = parts(factor);
  const digits = `${int}${frac}`.replace(/^0+/, "") || "0";
  const scale = frac.length;

  const divisor = Number(digits);
  if (divisor === 0 || scale > MAX_DIVISION_SCALE) return null;

  const power = 10 ** scale;
  // Esatto solo se il divisore è una potenza di dieci divisa per un intero:
  // cioè se il resto è zero.
  if (power % divisor !== 0) return null;

  return String(power / divisor);
}

/**
 * Il testo di un valore per la ricerca o la card, senza interpretarlo.
 *
 * Un booleano si scrive come lo scrive il contratto, una selezione si unisce:
 * cercare ciò che si vede sullo schermo deve trovare la riga.
 */
export function displayValue(value: FieldValue): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (Array.isArray(value)) return value.join(" ");
  return value;
}
